<script setup lang="ts">
import { useRouteQuery } from '@vueuse/router'

const { ecommerce } = useAnalytics()
const router = useRouter()
const route = useRoute()
const { el: headerRef, isHeaderVisible } = useUiHeader()
const isSearchPage = computed(() => ((route?.name as string) ?? '').includes('search'))
const isCheckoutPage = computed(() => ((route?.name as string) ?? '').includes('checkout'))
const { t } = useI18n()
const localePath = useLocalePath()
const collectionsStore = useCollectionsStore()
const uiStore = useUiStore()

const searchQuery = useRouteQuery('q', '')
const searchQueryInput = ref(searchQuery.value)

watch(isSearchPage, (isSearchPage) => {
  if (!isSearchPage)
    searchQueryInput.value = ''
})

async function handleCartClick() {
  uiStore.openCartDrawer()
  ecommerce.viewCart()
}

function submitSearch(event: Event) {
  event.preventDefault()
  ecommerce.search(searchQueryInput.value)
  router.push({
    path: localePath('/search'),
    query: { q: searchQueryInput.value },
  })
}

function handleMenuClick() {
  nextTick(() => {
    uiStore.openNavigationDrawer()
  })
}

const activeOrderStore = useActiveOrderStore()
const totalQuantity = computed(() => activeOrderStore.activeOrder.data?.totalQuantity ?? 0)
</script>

<template>
  <Header ref="headerRef" :visible="isHeaderVisible">
    <template #logo>
      <div class="flex">
        <UiButton
          id="mobile-navigation"
          :aria-label="t('general.navigation.label')"
          variant="ghost"
          size="icon"
          class="relative z-100 text-primary"
          data-testid="mobile-navigation-button"
          @click="handleMenuClick"
          @touchstart.passive="handleMenuClick"
        >
          <Icon name="lucide:menu" class="text-xl" />
        </UiButton>

        <div class="my-auto ml0 mr-auto flex items-center gap-4">
          <NuxtLinkLocale
            to="/"
            class="ml2 mr3 w20 flex overflow-hidden md:w-auto xs:w-[8.75rem]"
            :aria-label="t('general.homepage.label')"
          >
            <StoreLogo class="h9 w-auto text-slate-900 dark:text-white" />
          </NuxtLinkLocale>

          <NuxtLinkLocale to="/collections/best-sellers" class="text-sm text-black uppercase hidden -ml2 md:block">
            {{ t('pages.title.best_sellers') }}
          </NuxtLinkLocale>
          <NuxtLinkLocale to="/collections/new-arrivals" class="text-sm text-black uppercase hidden md:block">
            {{ t('pages.title.new_arrivals') }}
          </NuxtLinkLocale>
        </div>
      </div>
    </template>

    <template #right>
      <form class="mx2 bg-gray-200 hidden md:block" @submit.prevent="submitSearch">
        <label for="searchbar" class="sr-only bg-gray-200">{{ t('general.search.label') }}</label>
        <div class="relative w-full items-center">
          <UiInput id="searchbar" v-model="searchQueryInput" class="pl10" />
          <span class="absolute start-0 inset-y-0 flex items-center justify-center px2">
            <Icon name="i-fluent:search-24-regular" class="text-xl" />
          </span>
        </div>
      </form>

      <div class="relative ml2 flex items-center rounded-md">
        <UiButton id="cart" :aria-label="t('cart.label')" variant="dark" data-testid="cart" @click="handleCartClick">
          <Icon name="i-mingcute:shopping-cart-1-line" class="text-xl text-white" />
        </UiButton>

        <ClientOnly>
          <span v-if="totalQuantity > 0" class="pointer-events-none absolute h6 w6 flex items-center justify-center rounded-full bg-white text-sm text-black -right-1 -top-1 md:-right-2">
            {{ totalQuantity }}
          </span>
        </ClientOnly>
      </div>
    </template>

    <template v-if="!isCheckoutPage" #navbar>
      <div class="flex flex-row items-center gap-x-2 lg:-m-x-1">
        <div class="flex grow content-center items-center of-hidden lg:flex lg:px8 lg:py-2 lg:py4 lg:-ml6 lg:space-x-8">
          <template v-if="collectionsStore.rootCollections.status === 'pending'">
            <NSkeleton v-for="i in 9" :key="i" :style="{ width: `${100 + (i * i + i) % 7 * 20}px` }" pulse />
          </template>
          <template v-else-if="collectionsStore.rootCollections.data && collectionsStore.rootCollections.data?.length">
            <NuxtLinkLocale
              v-for="collection in collectionsStore.rootCollections.data.slice(0, 9)"
              :key="collection.id"
              :to="`/collections/${collection.id}/${collection.slug}`"
              n="sm slate9 dark:slate2"
              class="whitespace-nowrap text-context fw500 n-transition"
            >
              {{ collection.name }}
            </NuxtLinkLocale>
          </template>
        </div>
      </div>
    </template>

    <!-- <template v-if="!isCheckoutPage" #search >
      <div class="w-full">
        <form class="bg-gray-200" @submit.prevent="submitSearch">
          <label for="search" class="sr-only">{{ t('general.search.label') }}</label>
          <div class="relative w-full items-center">
           <UiInput id="search" v-model="searchQueryInput" class="pl10" />
           <span class="absolute start-0 inset-y-0 flex items-center justify-center px2">
             <Icon name="i-heroicons:magnifying-glass" />
           </span>
          </div>
        </form>
      </div>
    </template> -->
  </Header>
</template>
